import React, {useState} from 'react';
import {StyleSheet, View} from 'react-native';
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useTranslation} from 'react-i18next';

import {saveDeviceSettings} from '@/entities/device/api';
import type {TerminalSettings} from '@/entities/device/types';
import {messageOf} from '@/shared/lib/errors';
import {Banner} from '@/shared/ui/banner';
import {AppButton} from '@/shared/ui/button';
import {Card} from '@/shared/ui/card';
import {AppInput} from '@/shared/ui/input';
import {SectionHeader} from '@/shared/ui/section';
import {Segmented} from '@/shared/ui/segmented';
import {AppText} from '@/shared/ui/text';
import {Toggle} from '@/shared/ui/toggle';
import {spacing} from '@/shared/ui/theme';

/**
 * Per-device terminal settings. The whole TerminalSettings object is held in a draft
 * and saved as a full device override — fields the form does not expose (logo, admin
 * password, lens facing …) round-trip unchanged, so a save never silently resets them.
 * The terminal pulls the merged result on its next sync and hot-applies it.
 *
 * Five cards, one per subsystem, instead of five title-plus-loose-controls stacks: on a
 * form this long the card edge is what tells you where "Распознавание" stops and
 * "Реле" begins.
 *
 * NumberField and the camera segment are declared at MODULE SCOPE. They used to be
 * arrow functions inside the component, which makes them a new component type on every
 * keystroke — React then unmounts and remounts the field, and the keyboard closes after
 * each digit typed into it.
 */
const styles = StyleSheet.create({
  root: {gap: spacing(5), paddingBottom: spacing(4)},
  section: {gap: spacing(2)},
  card: {gap: spacing(4)},
  grid: {flexDirection: 'row', flexWrap: 'wrap', gap: spacing(3)},
  gridItem: {flexBasis: '46%', flexGrow: 1},
  field: {gap: spacing(2)},
});

function clampInt(text: string, min: number, max: number): number {
  const n = parseInt(text.replace(/[^\d]/g, ''), 10);
  if (!Number.isFinite(n)) {
    return min;
  }
  return Math.min(max, Math.max(min, n));
}

function NumberField({
  label,
  value,
  min,
  max,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (value: number) => void;
}) {
  return (
    <AppInput
      label={label}
      value={String(value)}
      onChangeText={text => onChange(clampInt(text, min, max))}
      keyboardType="number-pad"
      style={styles.gridItem}
    />
  );
}

const CAMERA_SOURCES = ['rgb', 'ir'] as const;
type CameraSource = (typeof CAMERA_SOURCES)[number];

export function TerminalSettingsForm({
  companyId,
  deviceId,
  settings,
}: {
  companyId: string;
  deviceId: string;
  /** The device's current effective settings — the draft seed. */
  settings: TerminalSettings;
}) {
  const {t} = useTranslation();
  const qc = useQueryClient();
  const [draft, setDraft] = useState<TerminalSettings>(settings);

  function set<K extends keyof TerminalSettings>(key: K, value: TerminalSettings[K]) {
    setDraft(d => ({...d, [key]: value}));
  }

  const save = useMutation({
    mutationFn: () => saveDeviceSettings(companyId, deviceId, draft),
    onSuccess: () => {
      void qc.invalidateQueries({queryKey: ['device', companyId, deviceId]});
    },
  });

  return (
    <View style={styles.root}>
      <View style={styles.section}>
        <SectionHeader title={t('terminal.sectionRecognition')} />
        <Card padding={4} style={styles.card}>
          <View style={styles.grid}>
            <NumberField
              label={t('terminal.accuracy')}
              value={draft.recognition_accuracy_percent}
              min={1}
              max={100}
              onChange={v => set('recognition_accuracy_percent', v)}
            />
            <NumberField
              label={t('terminal.confirmFrames')}
              value={draft.confirm_frames}
              min={1}
              max={30}
              onChange={v => set('confirm_frames', v)}
            />
            <NumberField
              label={t('terminal.cooldown')}
              value={draft.recognition_cooldown_seconds}
              min={0}
              max={60}
              onChange={v => set('recognition_cooldown_seconds', v)}
            />
          </View>
          <Toggle
            label={t('terminal.liveness')}
            hint={t('terminal.livenessHint')}
            value={draft.liveness_enabled}
            onChange={v => set('liveness_enabled', v)}
          />
          <Toggle
            label={t('terminal.photoLiveness')}
            value={draft.photo_liveness_enabled}
            onChange={v => set('photo_liveness_enabled', v)}
          />
          <Toggle
            label={t('terminal.spoof')}
            value={draft.spoof_detection}
            onChange={v => set('spoof_detection', v)}
          />
        </Card>
      </View>

      <View style={styles.section}>
        <SectionHeader title={t('terminal.sectionCamera')} />
        <Card padding={4}>
          <View style={styles.field}>
            <AppText variant="label">{t('terminal.cameraSource')}</AppText>
            <Segmented
              options={CAMERA_SOURCES.map(src => ({
                value: src,
                label: t(`terminal.camera.${src}`),
              }))}
              value={draft.camera_source as CameraSource}
              onChange={v => set('camera_source', v)}
            />
          </View>
        </Card>
      </View>

      <View style={styles.section}>
        <SectionHeader title={t('terminal.sectionRelay')} />
        <Card padding={4}>
          <View style={styles.grid}>
            <NumberField
              label={t('terminal.doorOpen')}
              value={draft.door_open_seconds}
              min={1}
              max={60}
              onChange={v => set('door_open_seconds', v)}
            />
            <NumberField
              label={t('terminal.nfcTimeout')}
              value={draft.nfc_timeout_ms}
              min={500}
              max={30000}
              onChange={v => set('nfc_timeout_ms', v)}
            />
          </View>
        </Card>
      </View>

      <View style={styles.section}>
        <SectionHeader title={t('terminal.sectionSignals')} />
        <Card padding={4} style={styles.card}>
          <Toggle
            label={t('terminal.denyLed')}
            hint={t('terminal.denyLedHint')}
            value={draft.deny_led_red}
            onChange={v => set('deny_led_red', v)}
          />
          <Toggle
            label={t('terminal.audio')}
            value={draft.audio_enabled}
            onChange={v => set('audio_enabled', v)}
          />
        </Card>
      </View>

      <View style={styles.section}>
        <SectionHeader title={t('terminal.sectionOther')} />
        <Card padding={4} style={styles.card}>
          <Toggle
            label={t('terminal.kiosk')}
            value={draft.kiosk_mode_enabled}
            onChange={v => set('kiosk_mode_enabled', v)}
          />
          <Toggle
            label={t('terminal.logDenials')}
            value={draft.log_denials}
            onChange={v => set('log_denials', v)}
          />
          <Toggle
            label={t('terminal.gpu')}
            value={draft.gpu_acceleration}
            onChange={v => set('gpu_acceleration', v)}
          />
        </Card>
      </View>

      {save.isError ? (
        <Banner tone="error" title={messageOf(save.error, t('errors.unknown'))} />
      ) : null}
      {save.isSuccess ? <Banner tone="success" title={t('terminal.saved')} /> : null}

      <AppButton
        title={t('actions.save')}
        size="lg"
        onPress={() => save.mutate()}
        loading={save.isPending}
      />
    </View>
  );
}
